import watermarkLogo from "@/assets/watermark-logo.png";
import { useLocation } from "react-router-dom";

/**
 * Site-wide watermark — fixed, semi-transparent brand logo overlay.
 *
 * Two modes:
 *  - "tiled"   (default): logo repeats softly across the whole viewport
 *  - "center" : single centered mark
 *
 * Accessibility & export safety:
 *  - `aria-hidden` + `role="presentation"` → invisible to screen readers
 *  - `pointer-events-none` → never blocks clicks
 *  - `print:hidden` Tailwind class + global `@media print { #aw-watermark }`
 *    rule → never appears in print or browser "Save as PDF"
 *  - Hidden on admin/utility routes by default
 *
 * Adaptive contrast:
 *  - Renders TWO stacked layers — a `multiply` layer (visible on light bg)
 *    and a `screen` layer (visible on dark bg). Whichever side has more
 *    contrast naturally shows through, so the mark stays consistently
 *    visible across hero (dark green) and content (white) sections without
 *    any JS background sniffing.
 */
interface WatermarkProps {
  variant?: "tiled" | "center";
  /** Tile size in px. Larger = sparser pattern. */
  tileSize?: number;
  /** 0–1. Per-layer opacity. Kept low so it never distracts. */
  opacity?: number;
  /** Force-render even on excluded routes. */
  force?: boolean;
}

// Routes where watermark would interfere (PDF previews, admin tools, etc.)
const EXCLUDED_ROUTES = ["/brochure", "/admin", "/email-test", "/new-year"];

const Watermark = ({
  variant = "tiled",
  tileSize = 340,
  opacity = 0.008,
  force = false,
}: WatermarkProps) => {
  const location = useLocation();
  const isExcluded =
    !force && EXCLUDED_ROUTES.some((r) => location.pathname.startsWith(r));
  if (isExcluded) return null;

  // Belt-and-suspenders: a global style block guarantees the watermark is
  // gone in print / Save-as-PDF even if Tailwind's `print:hidden` is
  // stripped or overridden by a parent stacking context.
  const printGuard = (
    <style>{`
      @media print {
        #aw-watermark, #aw-watermark * { display: none !important; }
      }
    `}</style>
  );

  if (variant === "center") {
    return (
      <>
        {printGuard}
        <div
          id="aw-watermark"
          aria-hidden="true"
          role="presentation"
          className="pointer-events-none fixed inset-0 z-[1] overflow-hidden print:hidden"
        >
          {/* Multiply layer — visible on light backgrounds */}
          <div className="absolute inset-0 flex items-center justify-center mix-blend-multiply">
            <img
              src={watermarkLogo}
              alt=""
              loading="lazy"
              draggable={false}
              style={{ opacity }}
              className="w-[60vw] max-w-[520px] min-w-[260px] select-none"
            />
          </div>
          {/* Screen layer — visible on dark backgrounds */}
          <div className="absolute inset-0 flex items-center justify-center mix-blend-screen">
            <img
              src={watermarkLogo}
              alt=""
              loading="lazy"
              draggable={false}
              style={{ opacity: opacity * 0.85 }}
              className="w-[60vw] max-w-[520px] min-w-[260px] select-none"
            />
          </div>
        </div>
      </>
    );
  }

  const tileStyle = {
    backgroundImage: `url(${watermarkLogo})`,
    backgroundRepeat: "repeat" as const,
    backgroundSize: `${tileSize}px ${tileSize}px`,
    backgroundPosition: "center",
    transform: "rotate(-18deg) scale(1.4)",
    transformOrigin: "center" as const,
  };

  return (
    <>
      {printGuard}
      <div
        id="aw-watermark"
        aria-hidden="true"
        role="presentation"
        className="pointer-events-none fixed inset-0 z-[1] overflow-hidden print:hidden select-none"
      >
        {/* Multiply tile — pops on light/white sections */}
        <div
          className="absolute inset-[-20%] mix-blend-multiply"
          style={{ ...tileStyle, opacity }}
        />
        {/* Screen tile — pops on dark hero/footer sections */}
        <div
          className="absolute inset-[-20%] mix-blend-screen"
          style={{ ...tileStyle, opacity: opacity * 0.9 }}
        />
      </div>
    </>
  );
};

export default Watermark;